// A record's position grid, as the requisition's (SPEC §9): its columns and their order are the
// user's own, kept per user on the server, ticked in "Columns" and moved by dragging a header; a
// header click sorts by that column, then reverses, then returns to position order.
import { useRef, useState, type HTMLAttributes, type ReactNode } from "react";
import { formatUsdFull } from "@po/shared";
import { useUserPref } from "../lib/prefs";
import { ColumnPicker } from "./DataTable";

export type GridCol<T> = {
  key: string;
  label: string;
  right?: boolean;
  /** Wide, wrapping text (remarks). */
  wrap?: boolean;
  cell: (l: T) => ReactNode;
  /** What the column sorts by. */
  value: (l: T) => string | number | boolean | null | undefined;
};

const empty = (v: unknown) => v == null || v === "";
/** Numbers by size, text by its digits too ("10" after "9"); empty last. */
function compare(a: unknown, b: unknown): number {
  if (empty(a)) return empty(b) ? 0 : 1;
  if (empty(b)) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

export function LineGrid<T extends { positionNo: number }>({
  lines,
  cols,
  defaults,
  prefKey,
  total,
  rowClass,
  cellProps,
}: {
  lines: T[];
  /** Every column the grid can show; `positionNo` is always shown. */
  cols: GridCol<T>[];
  /** The columns shown until the user picks their own. */
  defaults: string[];
  /** The user preference holding the picked columns in order. */
  prefKey: string;
  /** A Total row under one USD column, when shown. */
  total?: { key: string; usd: number; note?: ReactNode };
  rowClass?: (l: T) => string;
  cellProps?: (c: GridCol<T>, l: T) => HTMLAttributes<HTMLTableCellElement>;
}) {
  const [pref, setPref] = useUserPref<string[]>(prefKey);
  const known = (pref ?? defaults).filter((k) => cols.some((c) => c.key === k));
  const order = known.includes("positionNo") ? known : ["positionNo", ...known];
  const shown = new Set(order);
  const visible = order.map((k) => cols.find((c) => c.key === k)!);
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const dragged = useRef<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const sortBy = (key: string) => setSort((s) => (s?.key !== key ? { key, dir: 1 } : s.dir === 1 ? { key, dir: -1 } : null));
  // Dropped on a column: placed where that column was, so it lands on the side it came from.
  const move = (key: string, onto: string) => {
    if (key === onto) return;
    const rest = order.filter((k) => k !== key);
    const at = rest.indexOf(onto) + (order.indexOf(key) < order.indexOf(onto) ? 1 : 0);
    setPref([...rest.slice(0, at), key, ...rest.slice(at)]);
  };
  const sortCol = sort && visible.find((c) => c.key === sort.key);
  const rows = sortCol
    ? [...lines].sort((a, b) => {
        const va = sortCol.value(a);
        const vb = sortCol.value(b);
        // Empty stays last either way; ties keep position order.
        if (empty(va) || empty(vb)) return compare(va, vb) || a.positionNo - b.positionNo;
        return compare(va, vb) * sort!.dir || a.positionNo - b.positionNo;
      })
    : lines;
  const at = total ? visible.findIndex((c) => c.key === total.key) : -1;
  return (
    <>
      <div className="flex items-center gap-2 px-4 pt-3 pb-2">
        <h3 className="flex items-center gap-2 text-[17px] text-ink flex-1">
          Positions <span className="tnum rounded-full bg-hover border border-line px-2 text-[13px] leading-6">{lines.length}</span>
        </h3>
        <span className="text-[11.5px] text-muted hidden md:inline">Drag a column title to move it</span>
        <ColumnPicker
          options={cols}
          shown={shown}
          locked="positionNo"
          className="text-[12.5px] text-brand border border-line rounded-input px-2 py-0.5"
          onChange={(key, on) => setPref(on ? [...order, key] : order.filter((k) => k !== key))}
          onReset={() => setPref(null)}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="border-b border-line">
              {visible.map((c) => (
                <th
                  key={c.key}
                  draggable
                  onDragStart={(e) => {
                    dragged.current = c.key;
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => {
                    if (!dragged.current) return;
                    e.preventDefault();
                    setOver(c.key);
                  }}
                  onDragLeave={() => setOver((o) => (o === c.key ? null : o))}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragged.current) move(dragged.current, c.key);
                    dragged.current = null;
                    setOver(null);
                  }}
                  onDragEnd={() => {
                    dragged.current = null;
                    setOver(null);
                  }}
                  aria-sort={sort?.key === c.key ? (sort.dir === 1 ? "ascending" : "descending") : undefined}
                  className={`col px-3 py-2 text-left first:pl-4 last:pr-4 cursor-grab ${c.right ? "text-right" : ""} ${over === c.key ? "bg-hover" : ""}`}
                >
                  <button className={`group inline-flex items-center gap-1 ${c.right ? "flex-row-reverse" : ""}`} onClick={() => sortBy(c.key)} title="Sort; drag to move">
                    {c.label}
                    <span aria-hidden className={sort?.key === c.key ? "text-ink" : "text-transparent group-hover:text-muted"}>
                      {sort?.key === c.key && sort.dir === -1 ? "▼" : "▲"}
                    </span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.positionNo} className={`border-b border-line align-top ${rowClass?.(l) ?? "text-ink2"}`}>
                {visible.map((c) => (
                  <td
                    key={c.key}
                    className={`px-3 py-[5px] first:pl-4 last:pr-4 ${c.right ? "text-right tnum whitespace-nowrap" : ""} ${c.wrap ? "min-w-[160px] whitespace-pre-line" : ""} ${c.key === "name" ? "min-w-[220px]" : ""}`}
                    {...cellProps?.(c, l)}
                  >
                    {c.cell(l)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {total && at >= 0 && (
            <tfoot>
              <tr className="font-semibold text-ink">
                {at > 0 && (
                  <td colSpan={at} className="px-3 py-[6px] pl-4 text-right">
                    Total{total.note}
                  </td>
                )}
                <td className="px-3 py-[6px] text-right tnum whitespace-nowrap">{formatUsdFull(total.usd)}</td>
                {visible.length - at - 1 > 0 && <td colSpan={visible.length - at - 1} />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
}
